import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import { TranslocoModule } from '@jsverse/transloco';

import {
  FilterState, TableConfig, TableData, TablePage, TableStatus,
} from '../../../models/table.model';
import { HasValuePipe } from '../../../pipes/has-value.pipe';
import { ButtonComponent } from '../../button/button.component';
import { CheckboxComponent, CheckboxValue } from '../../checkbox/checkbox.component';
import { InlineSvgComponent } from '../../inline-svg/inline-svg.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { TableFilterComponent } from '../table-filter/table-filter.component';
import { TablePaginatorComponent } from '../table-paginator/table-paginator.component';

@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ButtonComponent,
    CheckboxComponent,
    TableFilterComponent,
    TablePaginatorComponent,
    InlineSvgComponent,
    SpinnerComponent,
    HasValuePipe,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  providers: [],
})
export class TableLayoutComponent implements AfterViewInit {
  config = input.required<TableConfig>();
  data = input<TableData<any>>([]);
  status = input<TableStatus>('loaded');
  divided = input<boolean>(false);

  template = input.required<TemplateRef<any>>();
  emptyTemplate = input<TemplateRef<any>>();

  checkedValues = model<any>([]);
  triggerOpenOptions = output<any>();
  isLoading = output<boolean>();

  filterState = model<FilterState | undefined>();

  /** Only read when `config.pagination` is set; the paginator shows once a page is bound. */
  page = model<TablePage | undefined>();
  totalCount = input<number>(0);

  currentOption = signal<any>(undefined);

  checkAllValue = computed<CheckboxValue>(() => {
    const data = this.data();
    const checked = this.checkedValues() as unknown[];
    const selected = data.filter((item) => checked.includes(item)).length;

    if (!selected) {
      return false;
    }

    return selected === data.length ? true : 'partial';
  });

  cssStyle = computed(() => {
    const config = this.config();
    const grid = config.filter.grid || `repeat(${config.columns}, 1fr)`;
    const checkbox = config.checkbox ? 'min-content ' : '';
    const rowOptions = (config.rowOptions ?? true) ? ' min-content' : '';

    return {
      'grid-template-columns': `${checkbox}${grid}${rowOptions}`,
    };
  });

  private scrollElement = viewChild.required<ElementRef>('scroll');
  private destroyRef = inject(DestroyRef);

  ngAfterViewInit() {
    const observer = new IntersectionObserver(
      ([ entry ]) => {
        // A paged table never loads more: its rows are replaced, not appended.
        if (entry.isIntersecting && this.isLoading && !this.config().pagination) {
          this.isLoading.emit(true);
        }
      },
      {
        root: null,
        threshold: 0.1,
      }
    );
    observer.observe(this.scrollElement().nativeElement);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  onChangeUpdateCheckbox(data: any): void {
    this.checkedValues.update((values: any[]) => {
      const index = values.indexOf(data);

      return index > -1 ? values.filter((item) => item !== data) : [ ...values, data ];
    });
  }

  onChangeCheckAll(): void {
    const selectAll = this.checkAllValue() !== true;
    this.checkedValues.set(selectAll ? [ ...this.data() ] : []);
  }

  onClickOpenOptions(data: any): void {
    this.currentOption.set(this.currentOption() === data ? null : data);
    this.triggerOpenOptions.emit(this.currentOption());
  }
}
